/**
 * AbriCare ZIP Locator — configuration and copy.
 *
 * Everything that a content editor or a translator might need to change lives
 * here, so the logic in zip-locator.js never contains a user-visible string.
 */
window.ABRICARE_LOCATOR_CONFIG = {
  /**
   * Mount path of the Webflow Cloud app. NOT locale-prefixed the way CMS pages
   * are, so /es/locations still calls /locator/api/... A wrong value surfaces
   * in the console as `bad_mount_path`. Override per page with
   * data-locator-base on the search container.
   */
  basePath: "/locator",

  /** How many locations a search leaves visible. */
  resultLimit: 5,

  /** Never hide every location -- which is why there is no distance cut-off. */
  minResults: 1,

  /** Attribute names, without the data-ms-code= prefix. */
  attrs: {
    item: "location-item",
    name: "location-name",
    address: "location-address",
    lat: "location-lat",
    lng: "location-lng",
    distance: "location-distance",
    input: "zip-input",
    submit: "zip-submit",
    reset: "zip-reset",
    message: "zip-message",
  },

  /** Finsweet accordion integration. */
  accordion: {
    /** Which elements are accordions, and so which location-items are results. */
    selector: '[fs-accordion-element="accordion"]',
    activeClass: "is-active-accordion",
    /**
     * Where Finsweet v1.1.2 binds its click handler (see webflow/vendor/).
     * Clicking it is the ONLY way to open an accordion -- see
     * docs/ARCHITECTURE.md. If a Finsweet upgrade changes this markup, the
     * script warns in the console; update the selector here.
     */
    triggerSelector: '[fs-accordion-element="trigger"]',
  },

  /**
   * Copy, keyed by locale: <html lang>, else the first path segment, else "en".
   * {count}, {zip} and {miles} are substituted at render time.
   */
  strings: {
    en: {
      invalidZip: "Please enter a 5-digit ZIP code.",
      notFound: "We couldn't find that ZIP code. Please check and try again.",
      unavailable: "Search is unavailable right now. All locations are shown below.",
      searching: "Searching…",
      results: "Showing the {count} closest locations to {zip}.",
      resultsOne: "Showing the closest location to {zip}.",
      reset: "Showing all locations.",
      distance: "{miles} mi",
    },
    es: {
      invalidZip: "Introduzca un código postal de 5 dígitos.",
      notFound: "No encontramos ese código postal. Verifíquelo e inténtelo de nuevo.",
      unavailable:
        "La búsqueda no está disponible en este momento. A continuación se muestran todas las ubicaciones.",
      searching: "Buscando…",
      results: "Mostrando las {count} ubicaciones más cercanas a {zip}.",
      resultsOne: "Mostrando la ubicación más cercana a {zip}.",
      reset: "Mostrando todas las ubicaciones.",
      distance: "{miles} mi",
    },
  },

  /**
   * How long to wait for the map before searching without it -- the accordions
   * carry the full address, so a ranked list alone is a complete locator.
   */
  mapReadyTimeoutMs: 4000,

  /** Set true to log decisions to the console. */
  debug: false,
};


/**
 * AbriCare ZIP Locator — front-end search.
 *
 * Load AFTER memberscript-181-patched.js. The map is a progressive
 * enhancement: with window.__locator present this also drives markers and the
 * camera, and without it the locator still filters, ranks and reorders.
 *
 * The only server call is ZIP -> coordinates; distance maths runs here against
 * coordinates the page already carries. See docs/ARCHITECTURE.md.
 */
(function () {
  "use strict";

  var CONFIG = window.ABRICARE_LOCATOR_CONFIG || {};
  var ATTRS = CONFIG.attrs || {};
  var EARTH_RADIUS_MILES = 3958.8;

  function log() {
    if (!CONFIG.debug) return;
    var args = Array.prototype.slice.call(arguments);
    console.log.apply(console, ["[locator]"].concat(args));
  }

  function sel(code) {
    return '[data-ms-code="' + code + '"]';
  }

  function text(root, code) {
    if (!root) return "";
    // textContent reads display:none elements, which is why the CMS bindings
    // are hidden text nodes rather than custom attributes.
    var el = root.querySelector(sel(code));
    return el ? (el.textContent || "").trim() : "";
  }

  // ---------------------------------------------------------------- strings

  function detectLocale() {
    var htmlLang = (document.documentElement.getAttribute("lang") || "").toLowerCase();
    if (htmlLang) {
      var base = htmlLang.split("-")[0];
      if (CONFIG.strings && CONFIG.strings[base]) return base;
    }
    var first = window.location.pathname.split("/")[1];
    if (first && CONFIG.strings && CONFIG.strings[first]) return first;
    return "en";
  }

  var LOCALE = detectLocale();

  function t(key, vars) {
    var table = (CONFIG.strings && (CONFIG.strings[LOCALE] || CONFIG.strings.en)) || {};
    var value = table[key] || (CONFIG.strings && CONFIG.strings.en && CONFIG.strings.en[key]) || "";
    if (!vars) return value;
    return value.replace(/\{(\w+)\}/g, function (match, name) {
      return Object.prototype.hasOwnProperty.call(vars, name) ? String(vars[name]) : match;
    });
  }

  // ------------------------------------------------------------ coordinates

  function toRadians(deg) {
    return (deg * Math.PI) / 180;
  }

  /** Great-circle distance in miles. */
  function haversineMiles(a, b) {
    var dLat = toRadians(b.lat - a.lat);
    var dLng = toRadians(b.lng - a.lng);
    var h =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRadians(a.lat)) *
        Math.cos(toRadians(b.lat)) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2);
    return 2 * EARTH_RADIUS_MILES * Math.asin(Math.min(1, Math.sqrt(h)));
  }

  /**
   * Repair lat/lng transposed in the CMS. A "latitude" outside [-90, 90] is a
   * longitude in the wrong field -- it has happened here before, and the CMS
   * fields are free text. Swap and warn rather than rank the office into the
   * ocean or hide it entirely.
   */
  function normalizeCoordinates(lat, lng, label) {
    if (!isFinite(lat) || !isFinite(lng)) return null;

    if (Math.abs(lat) > 90 && Math.abs(lng) <= 90) {
      console.warn(
        '[locator] "' +
          label +
          '" has latitude ' +
          lat +
          ", which is out of range. " +
          "Latitude and longitude look transposed in the CMS; using them swapped. " +
          "Fix the CMS values -- this guard is a safety net, not the fix."
      );
      return { lat: lng, lng: lat };
    }

    if (Math.abs(lat) > 90 || Math.abs(lng) > 180) {
      console.warn(
        '[locator] "' + label + '" has unusable coordinates (' + lat + ", " + lng + "); skipping."
      );
      return null;
    }

    return { lat: lat, lng: lng };
  }

  // ----------------------------------------------------------- DOM sourcing

  function accordionSelector() {
    return (CONFIG.accordion && CONFIG.accordion.selector) || '[fs-accordion-element="accordion"]';
  }

  function readLocationsFromDom() {
    var all = Array.prototype.slice.call(document.querySelectorAll(sel(ATTRS.item)));

    // Only accordions are results. A non-accordion location-item (the page
    // once carried a hidden legacy Collection List) would otherwise rank as a
    // hidden entry and, sitting first in the DOM, become locations[0] -- whose
    // parent is the reorder target.
    var items = all.filter(function (el) {
      return el.matches(accordionSelector());
    });

    if (items.length === 0) {
      // Markup changed, or another page reusing the attributes. Fall back
      // rather than disabling search outright.
      if (all.length > 0) {
        console.warn(
          "[locator] no location-item matched " +
            accordionSelector() +
            "; falling back to every location-item on the page."
        );
      }
      items = all;
    } else if (items.length !== all.length) {
      log("ignoring", all.length - items.length, "location-item(s) that are not accordions");
    }

    var locations = [];

    items.forEach(function (item, index) {
      var name = text(item, ATTRS.name) || "Location " + (index + 1);
      var coords = normalizeCoordinates(
        parseFloat(text(item, ATTRS.lat)),
        parseFloat(text(item, ATTRS.lng)),
        name
      );
      if (!coords) return;

      locations.push({
        name: name,
        lat: coords.lat,
        lng: coords.lng,
        index: index,
        element: item,
        marker: null,
      });
    });

    return locations;
  }

  /**
   * Merge marker references from window.__locator onto the DOM-sourced list.
   * By element identity, never position: once the DOM is reordered for
   * ranking, "Nth accordion is the Nth location" is false.
   */
  function attachMarkers(locations, locator) {
    if (!locator || !locator.locations) return locations;

    locations.forEach(function (loc) {
      var match = null;
      for (var i = 0; i < locator.locations.length; i++) {
        var candidate = locator.locations[i];
        var el = candidate.element || (candidate.elements && candidate.elements[0]);
        if (!el) continue;
        if (el === loc.element || el.contains(loc.element) || loc.element.contains(el)) {
          match = candidate;
          break;
        }
      }
      if (match) {
        loc.marker = match.marker || null;
      } else {
        console.warn("[locator] no map marker matched the accordion for", loc.name);
      }
    });

    return locations;
  }

  // ------------------------------------------------------------- accordions

  function activeClass() {
    return (CONFIG.accordion && CONFIG.accordion.activeClass) || "is-active-accordion";
  }

  function isAccordionOpen(el) {
    return el.classList.contains(activeClass());
  }

  function accordionTrigger(el) {
    var selector = CONFIG.accordion && CONFIG.accordion.triggerSelector;
    if (!selector) return null;
    return el.querySelector(selector);
  }

  var warned = {};

  function warnOnce(key, message) {
    if (warned[key]) return;
    warned[key] = true;
    console.warn("[locator] " + message);
  }

  /**
   * Open or close one accordion by clicking Finsweet's own trigger, letting
   * Finsweet own its state, panel height and ARIA.
   *
   * There is deliberately no class-toggling fallback. Finsweet holds `isOpen`
   * in a closure and writes `is-active-accordion` FROM it onto four elements,
   * so setting the class does not open an accordion -- it only desynchronises
   * the library, and the next click then reads as the opposite action. If the
   * trigger selector ever stops matching, search still filters, ranks and
   * reorders; it just cannot open the nearest result, and says so once.
   */
  function setAccordion(el, shouldOpen) {
    if (isAccordionOpen(el) === shouldOpen) return;

    var trigger = accordionTrigger(el);
    if (!trigger) {
      warnOnce(
        "no-trigger",
        "no accordion trigger matched " +
          ((CONFIG.accordion && CONFIG.accordion.triggerSelector) || "(unset)") +
          "; results are still filtered and ranked, but cannot be opened. " +
          "Check accordion.triggerSelector against the current Finsweet markup."
      );
      return;
    }

    trigger.click();

    if (isAccordionOpen(el) !== shouldOpen) {
      warnOnce(
        "trigger-ignored",
        "clicking the accordion trigger did not change its state. " +
          "Finsweet's markup has probably changed -- check accordion.triggerSelector."
      );
    }
  }

  /**
   * Close every open accordion through setAccordion, NOT by stripping the
   * class. Finsweet holds its own `isOpen` and merely writes the class from
   * it, so a bare class strip leaves the library believing the accordion is
   * open and the later re-open click reads as "close" -- which is most
   * searches on a two-location site.
   */
  function closeAllAccordions(locations) {
    locations.forEach(function (loc) {
      if (isAccordionOpen(loc.element)) setAccordion(loc.element, false);
    });
  }

  /**
   * Mirror open state onto the trigger as aria-expanded. Finsweet v1.1.2 sets
   * role, tabindex and aria-controls but never aria-expanded, and a search
   * moves focus onto the trigger it just opened -- so without this the user
   * lands on a control whose state is unannounced. An observer, not a one-off
   * write, so user clicks stay in sync too.
   */
  function syncAriaExpanded(locations) {
    var observers = [];

    locations.forEach(function (loc) {
      var trigger = accordionTrigger(loc.element);
      if (!trigger) return;

      var apply = function () {
        trigger.setAttribute("aria-expanded", String(isAccordionOpen(loc.element)));
      };

      apply();
      var observer = new MutationObserver(apply);
      observer.observe(loc.element, { attributes: true, attributeFilter: ["class"] });
      observers.push(observer);
    });

    return observers;
  }

  // ------------------------------------------------------------------- init

  function boot() {
    var container = document.querySelector("[data-locator-base]");
    var basePath =
      (container && container.getAttribute("data-locator-base")) || CONFIG.basePath || "/locator";

    var input = document.querySelector(sel(ATTRS.input));
    var submit = document.querySelector(sel(ATTRS.submit));
    var resetButton = document.querySelector(sel(ATTRS.reset));
    var message = document.querySelector(sel(ATTRS.message));

    if (!input || !submit) {
      log("search UI not present on this page; nothing to do");
      return;
    }

    var locations = readLocationsFromDom();
    if (locations.length === 0) {
      console.warn("[locator] no locations found in the DOM; search disabled");
      return;
    }

    // Reordering by appendChild needs one shared parent -- on the real page
    // the Collection List's .w-dyn-items.
    var listParent = locations[0].element.parentElement;
    var mixedParents = locations.some(function (loc) {
      return loc.element.parentElement !== listParent;
    });
    if (mixedParents) {
      console.warn(
        "[locator] location accordions do not share a parent; distance " +
          "reordering is disabled to avoid moving them between containers."
      );
      listParent = null;
    }
    var originalOrder = locations.map(function (loc) {
      return loc.element;
    });

    var locator = null;
    var pendingRequest = null;

    syncAriaExpanded(locations);

    // Set here rather than in the Designer so a redesign cannot lose it.
    if (message) {
      message.setAttribute("role", "status");
      message.setAttribute("aria-live", "polite");
      message.setAttribute("aria-atomic", "true");
    }

    function setMessage(value) {
      if (message) message.textContent = value;
    }

    // ------------------------------------------------------------- map hook

    function adoptLocator(source) {
      if (!source) return;
      locator = source;
      attachMarkers(locations, locator);

      // A search may already have run -- the map is slower to arrive than a
      // visitor is to type a ZIP. Markers created after that search know
      // nothing about it, so every pin would be on the map while only the
      // ranked few accordions are visible. Re-apply the state the DOM is
      // already in rather than tracking a copy of it.
      //
      // Only when something is actually filtered: on a normal load nothing is
      // hidden, and #181 has just fitBounds()ed and captured the overview, so
      // refitting here would repeat that work and re-race its idle handler.
      var filtered = locations.some(function (loc) {
        return loc.element.style.display === "none";
      });
      if (filtered) reapplyMarkerVisibility();

      log("map adopted;", locations.length, "locations");
    }

    var adopted = false;
    var onLocatorReady = function () {
      if (adopted) return;
      adopted = true;
      adoptLocator(window.__locator);
    };

    if (window.__locator) {
      onLocatorReady();
    } else {
      // The listener is never removed. The timeout below only stops the
      // locator WAITING for the map; a map that shows up late must still be
      // adopted, or its pins stay permanently out of step with the results.
      window.addEventListener("locator:ready", onLocatorReady);
      window.setTimeout(function () {
        if (adopted) return;
        log("map has not initialised yet; running without it for now");
      }, CONFIG.mapReadyTimeoutMs || 4000);
    }

    // -------------------------------------------------------------- results

    function clearDistances() {
      locations.forEach(function (loc) {
        var slot = loc.element.querySelector(sel(ATTRS.distance));
        if (slot) slot.textContent = "";
      });
    }

    /**
     * Recompute the map's "all pins" overview so closing an accordion glides
     * back to the FILTERED bounds. The overview object is mutated, never
     * reassigned -- the accordion sync in #181 closed over that reference.
     */
    function refitMap(visible) {
      if (!locator || !locator.map || typeof window.google === "undefined") return;
      var withMarkers = visible.filter(function (loc) {
        return loc.marker && loc.marker.position;
      });
      if (withMarkers.length === 0) return;

      var bounds = new window.google.maps.LatLngBounds();
      withMarkers.forEach(function (loc) {
        bounds.extend(loc.marker.position);
      });
      locator.map.fitBounds(bounds);

      if (locator.overview) {
        window.google.maps.event.addListenerOnce(locator.map, "idle", function () {
          var center = locator.map.getCenter();
          if (center) locator.overview.center = center.toJSON();
          locator.overview.zoom = locator.map.getZoom();
        });
      }
    }

    function setMarkerVisible(loc, visible) {
      // AdvancedMarkerElement uses the `map` property; setMap() is the legacy
      // Marker API and is a no-op here.
      if (loc.marker) loc.marker.map = visible ? locator.map : null;
    }

    /**
     * Push the accordions' current visibility onto the map, and refit to it.
     *
     * Reads style.display off the DOM rather than a remembered result set, so
     * it is correct whether or not a search has run and cannot drift from what
     * the visitor can actually see. Called when the map is adopted, which may
     * be after a search has already filtered the list.
     */
    function reapplyMarkerVisibility() {
      var visible = [];

      locations.forEach(function (loc) {
        var isVisible = loc.element.style.display !== "none";
        setMarkerVisible(loc, isVisible);
        if (isVisible) visible.push(loc);
      });

      refitMap(visible);
    }

    /** Apply a ranked result set. The step order matters. */
    function applyResults(ranked, zip) {
      var visible = ranked.slice(0, Math.max(CONFIG.minResults || 1, CONFIG.resultLimit || 5));
      var visibleSet = new Set(
        visible.map(function (entry) {
          return entry.loc;
        })
      );

      // 1. Close first. Finsweet marks one accordion active on load; hiding it
      //    while still active leaves the camera on an invisible location.
      closeAllAccordions(locations);

      // 2. Hide with style.display, not a class: the camera sync observes
      //    class mutations, and display:none also drops the accordion from the
      //    tab order and a11y tree. Nodes stay in the document for reset.
      locations.forEach(function (loc) {
        var isVisible = visibleSet.has(loc);
        loc.element.style.display = isVisible ? "" : "none";
        setMarkerVisible(loc, isVisible);
      });

      // 3. Reorder real nodes, not CSS `order` -- tab order follows the DOM,
      //    so a keyboard user would otherwise traverse the old sequence.
      if (listParent) {
        visible.forEach(function (entry) {
          listParent.appendChild(entry.loc.element);
        });
      }

      // 4. Distances into the per-accordion slot.
      clearDistances();
      visible.forEach(function (entry) {
        var slot = entry.loc.element.querySelector(sel(ATTRS.distance));
        if (slot) slot.textContent = t("distance", { miles: entry.miles.toFixed(1) });
      });

      // 5. Refit the map and recompute the overview.
      refitMap(
        visible.map(function (entry) {
          return entry.loc;
        })
      );

      // 6. Open the nearest; the camera sync observer glides to it.
      var nearest = visible[0];
      if (nearest) {
        setAccordion(nearest.loc.element, true);
        focusAccordion(nearest.loc.element);
      }

      setMessage(
        visible.length === 1
          ? t("resultsOne", { zip: zip })
          : t("results", { count: visible.length, zip: zip })
      );
    }

    /** Move focus to the opened accordion so keyboard users land on the result. */
    function focusAccordion(el) {
      var target = accordionTrigger(el) || el;
      if (!target.hasAttribute("tabindex") && target === el) target.setAttribute("tabindex", "-1");
      try {
        target.focus({ preventScroll: false });
      } catch {
        // Older browsers reject the options object; the bare call still works.
        target.focus();
      }
    }

    function resetAll(announce) {
      if (pendingRequest) {
        pendingRequest.abort();
        pendingRequest = null;
      }

      closeAllAccordions(locations);

      locations.forEach(function (loc) {
        loc.element.style.display = "";
        setMarkerVisible(loc, true);
      });

      // appendChild destroyed the original order; originalOrder is the record.
      if (listParent) {
        originalOrder.forEach(function (el) {
          listParent.appendChild(el);
        });
      }

      clearDistances();
      refitMap(locations);
      setMessage(announce ? t("reset") : "");
    }

    // --------------------------------------------------------------- search

    function search(zip) {
      if (pendingRequest) pendingRequest.abort();

      var controller = new AbortController();
      pendingRequest = controller;
      setMessage(t("searching"));

      var url = basePath.replace(/\/$/, "") + "/api/zip/" + encodeURIComponent(zip);

      fetch(url, { signal: controller.signal, headers: { Accept: "application/json" } })
        .then(function (res) {
          var contentType = res.headers.get("content-type") || "";
          if (contentType.indexOf("application/json") === -1) {
            // Webflow served one of its own pages: the mount path is wrong.
            // Say so plainly rather than reporting a generic failure.
            console.error(
              "[locator] bad_mount_path: " +
                url +
                " returned " +
                (contentType || "no content type") +
                " instead of JSON. Check that the Webflow Cloud app is mounted at " +
                basePath +
                "."
            );
            throw new Error("bad_mount_path");
          }
          return res.json().then(function (body) {
            return { status: res.status, body: body };
          });
        })
        .then(function (result) {
          if (pendingRequest !== controller) return; // superseded by a newer search
          pendingRequest = null;

          if (result.status === 404) {
            setMessage(t("notFound"));
            return;
          }
          if (result.status === 400) {
            setMessage(t("invalidZip"));
            return;
          }
          if (result.status !== 200) {
            setMessage(t("unavailable"));
            return;
          }

          var origin = { lat: result.body.lat, lng: result.body.lng };
          var ranked = locations
            .map(function (loc) {
              return { loc: loc, miles: haversineMiles(origin, loc) };
            })
            .sort(function (a, b) {
              return a.miles - b.miles;
            });

          applyResults(ranked, zip);
        })
        .catch(function (err) {
          if (err && err.name === "AbortError") return;
          if (pendingRequest === controller) pendingRequest = null;
          console.error("[locator] search failed", err);
          // A broken search must never leave a blank locations page.
          resetAll(false);
          setMessage(t("unavailable"));
        });
    }

    function submitSearch() {
      var zip = (input.value || "").trim();
      if (!/^\d{5}$/.test(zip)) {
        setMessage(t("invalidZip"));
        input.focus();
        return;
      }
      search(zip);
    }

    // ---------------------------------------------------------------- wiring

    submit.addEventListener("click", function (event) {
      event.preventDefault();
      submitSearch();
    });

    input.addEventListener("keydown", function (event) {
      if (event.key === "Enter") {
        event.preventDefault();
        submitSearch();
      }
    });

    // A bare input + button, not a Webflow Form: those POST to their own
    // endpoint and their states fight this script. If the markup lands inside
    // a form anyway, stop it navigating.
    var owningForm = input.closest("form");
    if (owningForm) {
      owningForm.addEventListener("submit", function (event) {
        event.preventDefault();
        submitSearch();
      });
    }

    if (resetButton) {
      resetButton.addEventListener("click", function (event) {
        event.preventDefault();
        input.value = "";
        resetAll(true);
        input.focus();
      });
    }

    // QA surface, used by docs/VERIFICATION.md.
    window.__abricareLocator = {
      search: search,
      reset: function () {
        resetAll(true);
      },
      locations: locations,
      /** How many accordions have a working Finsweet trigger. QA: expect all. */
      get accordionTriggersFound() {
        return locations.filter(function (loc) {
          return Boolean(accordionTrigger(loc.element));
        }).length;
      },
      config: CONFIG,
      basePath: basePath,
      locale: LOCALE,
    };

    log("ready:", locations.length, "locations, locale", LOCALE, "base", basePath);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
